'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const oceanPaymentFormSchema = z.object({
  hideCardForm: z.boolean(),
  sandboxed: z.boolean(),
  account: z.string().min(1, 'Account is required'),
  terminal: z.string().min(1, 'Terminal is required'),
  secureCode: z.string().min(1, 'Secure Code is required'),
  // Optional: Ocean's public key turns on the inline card form in checkout.
  key: z.string(),
})

type FormValues = z.infer<typeof oceanPaymentFormSchema>

interface OceanPaymentFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function OceanPaymentForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: OceanPaymentFormProps) {
  const { t } = useTranslation('integrations')
  // Show bullets if existing credentials exist
  const hasExistingAccount =
    defaultValues.credentials?.account &&
    defaultValues.credentials.account.length > 0
  const hasExistingTerminal =
    defaultValues.credentials?.terminal &&
    defaultValues.credentials.terminal.length > 0
  const hasExistingSecureCode =
    defaultValues.credentials?.secureCode &&
    defaultValues.credentials.secureCode.length > 0
  const hasExistingKey =
    defaultValues.credentials?.key &&
    defaultValues.credentials.key.length > 0

  const form = useForm<FormValues>({
    resolver: zodResolver(oceanPaymentFormSchema),
    mode: 'all',
    defaultValues: {
      hideCardForm: defaultValues.credentials?.hideCardForm ?? true,
      sandboxed: defaultValues.credentials?.sandboxed ?? false,
      account: hasExistingAccount ? '•'.repeat(20) : '',
      terminal: hasExistingTerminal ? '•'.repeat(20) : '',
      secureCode: hasExistingSecureCode ? '•'.repeat(20) : '',
      key: hasExistingKey ? '•'.repeat(20) : '',
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { hideCardForm, sandboxed, account, terminal, secureCode, key } = data
    await onSubmit({
      credentials: {
        hideCardForm: touchedFields.hideCardForm ? hideCardForm : (defaultValues.credentials?.hideCardForm ?? true),
        sandboxed: touchedFields.sandboxed ? sandboxed : (defaultValues.credentials?.sandboxed ?? false),
        account: touchedFields.account ? account : (defaultValues.credentials?.account ?? ''),
        terminal: touchedFields.terminal ? terminal : (defaultValues.credentials?.terminal ?? ''),
        secureCode: touchedFields.secureCode ? secureCode : (defaultValues.credentials?.secureCode ?? ''),
        // Rebuilt on every save, so an untouched key must be carried over — and
        // clearing the field switches the checkout back to the redirect flow.
        key: touchedFields.key ? (key.trim() || undefined) : defaultValues.credentials?.key,
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Warning Alert */}
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('oceanpayment.importantNotice')}</AlertTitle>
        <AlertDescription>
          <div className="space-y-2 text-sm">
            <p>{t('oceanpayment.importantNoticeText1')}</p>
            <p>{t('oceanpayment.importantNoticeText2')}</p>
          </div>
        </AlertDescription>
      </Alert>

      {/* Payment Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t('oceanpayment.paymentSettings')}</CardTitle>
          <CardDescription>{t('oceanpayment.paymentSettingsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="hideCardForm" className="font-medium">
                {t('oceanpayment.hideCardForm')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('oceanpayment.hideCardFormHint')}
              </p>
            </div>
            <Switch
              id="hideCardForm"
              checked={watch('hideCardForm')}
              onCheckedChange={(checked) =>
                setValue('hideCardForm', checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                })
              }
              disabled={isLoading}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sandboxed" className="font-medium">
                {t('oceanpayment.sandboxMode')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('oceanpayment.sandboxModeHint')}
              </p>
            </div>
            <Switch
              id="sandboxed"
              checked={watch('sandboxed')}
              onCheckedChange={(checked) =>
                setValue('sandboxed', checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                })
              }
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('oceanpayment.apiCredentials')}</CardTitle>
          <CardDescription>{t('oceanpayment.apiCredentialsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="account">
              {t('oceanpayment.account')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="account"
              placeholder={t('oceanpayment.accountPlaceholder')}
              disabled={isLoading}
              {...register('account')}
            />
            {touchedFields.account && errors.account && (
              <p className="text-xs text-destructive">{errors.account.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="terminal">
              {t('oceanpayment.terminal')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="terminal"
              placeholder={t('oceanpayment.terminalPlaceholder')}
              disabled={isLoading}
              {...register('terminal')}
            />
            {touchedFields.terminal && errors.terminal && (
              <p className="text-xs text-destructive">{errors.terminal.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="secureCode">
              {t('oceanpayment.secureCode')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="secureCode"
              type="password"
              placeholder={t('oceanpayment.secureCodePlaceholder')}
              disabled={isLoading}
              {...register('secureCode')}
            />
            {touchedFields.secureCode && errors.secureCode && (
              <p className="text-xs text-destructive">{errors.secureCode.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="key">{t('oceanpayment.publicKey')}</Label>
            <Input
              id="key"
              type="password"
              placeholder={t('oceanpayment.publicKeyPlaceholder')}
              disabled={isLoading}
              {...register('key')}
            />
            <p className="text-xs text-muted-foreground">{t('oceanpayment.publicKeyHint')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('oceanpayment.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>{t('oceanpayment.setupStep1')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('oceanpayment.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('oceanpayment.setupStep3') }} />
              <li dangerouslySetInnerHTML={{ __html: t('oceanpayment.setupStep4') }} />
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
